<template>
  <div class="ten-time">
    <ten-time-options
      key="hour"
      v-if="formatInfo.hourFormat[0]"
      class="ten-time__item"
      :top-item-index="hourIndex"
      :option-list="hoursList"
      @change="onHourChange"
      @mouseenter="focusTimeInput(formatInfo.hourFormat[0], focusIndex.hourTypeIndex, dateObj.hours)"
    />

    <ten-time-options
      key="min"
      v-if="formatInfo.minFormat[0]"
      class="ten-time__item"
      :top-item-index="minuteIndex"
      :option-list="minutesList"
      @change="onMinuteChange"
      @mouseenter="focusTimeInput(formatInfo.minFormat[0], focusIndex.minTypeIndex, dateObj.minutes)"
    />

    <ten-time-options
      key="sec"
      v-if="formatInfo.secFormat[0]"
      class="ten-time__item"
      :top-item-index="secondIndex"
      :option-list="secondsList"
      @change="onSecondChange"
      @mouseenter="focusTimeInput(formatInfo.secFormat[0], focusIndex.secTypeIndex, dateObj.seconds)"
    />

    <ten-time-options
      key="mer"
      v-if="formatInfo.meridiemFormat[0]"
      class="ten-time__item"
      :top-item-index="meridiemIndex"
      :option-list="meridiemList"
      @change="onMeridiemChange"
      @mouseenter="focusTimeInput(formatInfo.meridiemFormat[0], focusIndex.meridiemTypeIndex, dateObj.meridiem)"
    />
  </div>
</template>

<script>
import TimeOptions from '../basic/time-options';

import {
  getMeridiem,
  getHours,
  getMintues,
  getSeconds,
  getDateObj,
  getFormatInfo,
  parseToDate,
  hour24To12 } from '../util';

function focusIndexNeedToFix(type, value) {
  return type.length < value.toString().length ? value.toString().length - type.length : 0;
}

function fixFocusIndex(tokens) {
  const result = {};

  tokens.reduce((accOffset, token, currIndex, src) => {
    const { name, index } = token;

    if (currIndex) {
      const prevToken = src[currIndex - 1];
      const { value, 0: type } = prevToken;
      const offset = focusIndexNeedToFix(type, value);
      if (offset || accOffset) {
        accOffset += offset;
        result[`${name}TypeIndex`] = accOffset + index;
        return accOffset;
      }
    }

    result[`${name}TypeIndex`] = index;
    return accOffset;
  }, 0);

  return result;
}

export default {
  components: {
    TenTimeOptions: TimeOptions,
  },
  inheritAttrs: false,
  props: {
    value: [Date, String],
    format: String,
    getRelatedInput: Function,
    disabledHour: Function,
    disabledMin: Function,
    disabledSec: Function,
    minTime: Date,
  },
  computed: {
    is12() {
      return /h{1,2}/.test(this.formatInfo.hourFormat[0]) && this.formatInfo.meridiemFormat[0];
    },
    dateObj() {
      return getDateObj(this.value);
    },
    formatInfo() {
      return getFormatInfo(this.format);
    },
    tokens() {
      return [
        { value: this.dateObj.hours, name: 'hour', ...this.formatInfo.hourFormat },
        { value: this.dateObj.minutes, name: 'min', ...this.formatInfo.minFormat },
        { value: this.dateObj.seconds, name: 'sec', ...this.formatInfo.secFormat },
        { value: this.dateObj.meridiem, name: 'meridiem', ...this.formatInfo.meridiemFormat },
      ].filter(v => v[0]).sort((a, b) => a.index - b.index);
    },
    focusIndex() {
      return fixFocusIndex(this.tokens);
    },
    minTimeObj() {
      return getDateObj(this.minTime);
    },
    hoursList() {
      return getHours(
        {
          hourType: this.formatInfo.hourFormat[0],
          meridiem: this.dateObj.meridiem,
        },
        {
          disabledHour: this.disabledHour,
          minH: this.minTimeObj.hours,
        },
        this.dateObj.hours
      );
    },
    minutesList() {
      return getMintues(
        this.dateObj.hours,
        {
          disabledMin: this.disabledMin,
          minH: this.minTimeObj.hours,
          minM: this.minTimeObj.minutes,
        },
        this.dateObj.minutes
      );
    },
    secondsList() {
      return getSeconds(
        {
          hours: this.dateObj.hours,
          minutes: this.dateObj.minutes,
        },
        {
          disabledSec: this.disabledSec,
          minH: this.minTimeObj.hours,
          minM: this.minTimeObj.minutes,
          minS: this.minTimeObj.seconds,
        },
        this.dateObj.seconds
      );
    },
    meridiemList() {
      return getMeridiem(
        this.formatInfo.meridiemFormat[0],
        {
          disabledHour: this.disabledHour,
          minH: this.minTimeObj.hours,
        },
        this.dateObj.meridiem
      );
    },
    hourIndex() {
      return this.hoursList.findIndex(v => v.selected);
    },
    minuteIndex() {
      return this.minutesList.findIndex(v => v.selected);
    },
    secondIndex() {
      return this.secondsList.findIndex(v => v.selected);
    },
    meridiemIndex() {
      return this.meridiemList.findIndex(v => v.selected);
    },
  },
  methods: {
    onHourChange(value) {
      this.onChangeHandler(this.formatInfo.hourFormat[0], value);
    },
    onMinuteChange(value) {
      this.onChangeHandler(this.formatInfo.minFormat[0], value);
    },
    onSecondChange(value) {
      this.onChangeHandler(this.formatInfo.secFormat[0], value);
    },
    onMeridiemChange(value) {
      this.onChangeHandler(this.formatInfo.meridiemFormat[0], value);
    },
    onChangeHandler(type, value) {
      const { year, month, date } = this.dateObj;

      const hours = ['HH', 'H'].indexOf(type) > -1 ? value : this.dateObj.hours;
      const hours12 = ['hh', 'h'].indexOf(type) > -1 ? value : hour24To12(this.dateObj.hours);
      const minutes = ['mm', 'm'].indexOf(type) > -1 ? value : this.dateObj.minutes;
      const seconds = ['ss', 's'].indexOf(type) > -1 ? value : this.dateObj.seconds;
      const meridiem = ['a', 'A'].indexOf(type) > -1 ? value : this.dateObj.meridiem;

      const { is12 } = this;

      const dateStr = `${year}-${month + 1}-${date} ${is12 ? hours12 : hours}:${minutes}:${seconds} ${is12 ? meridiem : ''}`;
      const format = `YYYY-M-D ${is12 ? 'h' : 'H'}:m:s ${is12 ? 'A' : ''}`;

      this.$emit('change', parseToDate(dateStr, format));
    },
    focusTimeInput(type, index, value) {
      const rawInput = this.getRelatedInput();
      rawInput.focus();
      rawInput.selectionStart = index;
      let len;
      if (typeof value === 'number') {
        len = type.length === 2 && value < 10 ? value.toString().length + 1 : value.toString().length;
      } else {
        len = value.length;
      }
      rawInput.selectionEnd = index + len;
    },
  },
};
</script>
